import { useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import {
  Truck, MapPin, Phone, CheckCircle2, XCircle, Camera, RotateCcw,
  PlayCircle, Banknote, X, AlertCircle, Package, Clock
} from "lucide-react";
import { toast } from "sonner";
import VenusLogo from "../components/VenusLogo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt = (n) => `${(Math.round(n) || 0).toLocaleString("fr-FR")} F`;

const STATUS = {
  en_attente: { label: "À démarrer", cls: "bg-slate-100 text-slate-700" },
  assigne: { label: "À démarrer", cls: "bg-slate-100 text-slate-700" },
  en_route: { label: "En route", cls: "bg-amber-100 text-amber-800" },
  livree: { label: "Livrée", cls: "bg-[#F8E8F1] text-[#8E3A6D]" },
  echec: { label: "Échec", cls: "bg-red-100 text-red-700" },
};

// ----- Signature pad -----
function SignaturePad({ onChange, initial }) {
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const last = useRef({ x: 0, y: 0 });

  useEffect(() => {
    const c = canvasRef.current;
    const ctx = c.getContext("2d");
    ctx.lineWidth = 2.5;
    ctx.lineCap = "round";
    ctx.strokeStyle = "#020A18";
    ctx.fillStyle = "#fff";
    ctx.fillRect(0, 0, c.width, c.height);
    if (initial) {
      const img = new Image();
      img.onload = () => ctx.drawImage(img, 0, 0, c.width, c.height);
      img.src = initial;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const pos = (e) => {
    const c = canvasRef.current;
    const r = c.getBoundingClientRect();
    const t = e.touches ? e.touches[0] : e;
    return { x: ((t.clientX - r.left) * c.width) / r.width, y: ((t.clientY - r.top) * c.height) / r.height };
  };
  const start = (e) => { e.preventDefault(); drawing.current = true; last.current = pos(e); };
  const move = (e) => {
    if (!drawing.current) return;
    e.preventDefault();
    const c = canvasRef.current;
    const ctx = c.getContext("2d");
    const p = pos(e);
    ctx.beginPath();
    ctx.moveTo(last.current.x, last.current.y);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
    last.current = p;
  };
  const end = () => {
    if (!drawing.current) return;
    drawing.current = false;
    onChange?.(canvasRef.current.toDataURL("image/png"));
  };
  const clear = () => {
    const c = canvasRef.current;
    const ctx = c.getContext("2d");
    ctx.fillStyle = "#fff";
    ctx.fillRect(0, 0, c.width, c.height);
    onChange?.(null);
  };

  return (
    <div>
      <canvas
        ref={canvasRef}
        width={600} height={200}
        className="w-full h-40 rounded-lg border-2 border-dashed border-[var(--border-line)] bg-white touch-none"
        onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
        onTouchStart={start} onTouchMove={move} onTouchEnd={end}
        data-testid="dp-signature-pad"
      />
      <button type="button" onClick={clear} className="mt-2 text-xs text-[var(--text-muted)] flex items-center gap-1 hover:text-[var(--text-base)]">
        <RotateCcw size={12}/> Effacer
      </button>
    </div>
  );
}

// ----- Delivery completion modal -----
function CompleteModal({ delivery, token, onClose, onDone }) {
  const stateKey = `dp-complete-${delivery.id}`;
  const [signature, setSignature] = useState(() => {
    try { return JSON.parse(sessionStorage.getItem(stateKey) || "{}").signature || null; } catch { return null; }
  });
  const [photo, setPhoto] = useState(() => {
    try { return JSON.parse(sessionStorage.getItem(stateKey) || "{}").photo || null; } catch { return null; }
  });
  const [notes, setNotes] = useState(() => {
    try { return JSON.parse(sessionStorage.getItem(stateKey) || "{}").notes || ""; } catch { return ""; }
  });
  const [paymentAmount, setPaymentAmount] = useState(() => {
    try {
      const saved = JSON.parse(sessionStorage.getItem(stateKey) || "{}").paymentAmount;
      return saved ?? String(delivery.balance || delivery.total || 0);
    } catch { return String(delivery.balance || delivery.total || 0); }
  });
  const [paymentMethod, setPaymentMethod] = useState(() => {
    try { return JSON.parse(sessionStorage.getItem(stateKey) || "{}").paymentMethod || "especes"; } catch { return "especes"; }
  });
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);

  // Persist state on every change so iOS can recover after camera intent
  useEffect(() => {
    try { sessionStorage.setItem(stateKey, JSON.stringify({ signature, photo, notes, paymentAmount, paymentMethod, openFor: delivery.id })); } catch {}
  }, [signature, photo, notes, paymentAmount, paymentMethod, stateKey, delivery.id]);

  const onPickPhoto = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    // Down-scale large images to <800kb base64 to avoid iOS PWA memory eviction
    const reader = new FileReader();
    reader.onload = () => {
      const img = new Image();
      img.onload = () => {
        const max = 1280;
        const ratio = Math.min(1, max / Math.max(img.width, img.height));
        const w = Math.round(img.width * ratio);
        const h = Math.round(img.height * ratio);
        const c = document.createElement("canvas");
        c.width = w; c.height = h;
        c.getContext("2d").drawImage(img, 0, 0, w, h);
        setPhoto(c.toDataURL("image/jpeg", 0.78));
      };
      img.onerror = () => setPhoto(reader.result);
      img.src = reader.result;
    };
    reader.readAsDataURL(f);
  };

  const closeAndClear = () => {
    try { sessionStorage.removeItem(stateKey); } catch {}
    onClose();
  };

  const submit = async () => {
    if (!signature && !photo) {
      if (!window.confirm("Aucune signature ni photo. Confirmer quand même la livraison ?")) return;
    }
    const amt = parseFloat(paymentAmount);
    const validAmount = !isNaN(amt) && amt > 0 ? amt : null;
    setSaving(true);
    try {
      await axios.post(`${API}/driver-portal/${token}/orders/${delivery.id}/complete-delivery`, {
        signature, proof_photo: photo, notes,
        payment_amount: validAmount,
        payment_method: paymentMethod,
        payment_note: validAmount ? "Encaissement à la livraison" : "",
      });
      try { sessionStorage.removeItem(stateKey); } catch {}
      toast.success(validAmount ? `Livraison + encaissement ${validAmount.toLocaleString("fr-FR")} F` : "Livraison confirmée");
      onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[60] flex justify-center p-4" onClick={closeAndClear} data-testid="dp-complete-modal">
      <div className="bg-white w-full sm:max-w-md shadow-2xl rounded-2xl flex flex-col overflow-hidden self-start mt-4 mb-4 max-h-[calc(100vh-2rem)]" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-4 border-b border-[var(--border-line)]">
          <div>
            <div className="font-heading font-extrabold text-lg">Confirmer la livraison</div>
            <div className="label-mono mt-0.5">{delivery.order_number}</div>
          </div>
          <button onClick={closeAndClear} className="w-9 h-9 rounded-full hover:bg-[var(--bg-soft,#f7f7f7)] flex items-center justify-center"><X size={20}/></button>
        </div>
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          <div>
            <div className="label-mono mb-2">Signature du client</div>
            <SignaturePad initial={signature} onChange={setSignature} />
          </div>
          <div>
            <div className="label-mono mb-2">Photo de preuve (optionnel)</div>
            <input ref={fileRef} type="file" accept="image/*" onChange={onPickPhoto} className="hidden" data-testid="dp-photo-input"/>
            <button type="button" onClick={() => fileRef.current?.click()} className="btn-ghost w-full flex items-center justify-center gap-2 text-sm">
              <Camera size={16}/> {photo ? "Changer la photo" : "Choisir / Prendre une photo"}
            </button>
            {photo && (
              <div className="mt-2 relative">
                <img src={photo} alt="" className="w-full h-32 object-cover rounded-lg" />
                <button onClick={() => setPhoto(null)} className="absolute top-1 right-1 w-7 h-7 rounded-full bg-black/60 text-white flex items-center justify-center"><X size={14}/></button>
              </div>
            )}
            <div className="text-[11px] text-[var(--text-muted)] mt-1">Astuce : choisissez la photo depuis votre galerie après l'avoir prise — c'est plus stable sur certains téléphones.</div>
          </div>
          <div>
            <label className="label-mono mb-1.5 block">Notes (optionnel)</label>
            <textarea rows={2} className="input-base" value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Remarque sur la livraison..." />
          </div>
          <div className="bg-[var(--primary-soft,#F8E8F1)] border border-[var(--primary,#C2569B)]/30 rounded-xl p-3 space-y-2">
            <div className="text-xs font-bold text-[var(--primary-dark,#8E3A6D)] uppercase tracking-wider">
              💰 Encaissement à la livraison
            </div>
            <div className="text-[11px] text-[var(--text-muted)] -mt-1">
              Si vous recevez de l'argent maintenant, indiquez le montant. Sinon laissez 0.
            </div>
            <div className="grid grid-cols-2 gap-2">
              <input
                data-testid="dp-payment-amount"
                type="number" min="0" step="100" inputMode="numeric"
                className="input-base font-data text-base font-semibold"
                value={paymentAmount}
                onChange={(e) => setPaymentAmount(e.target.value)}
                placeholder="Montant FCFA"
              />
              <select
                data-testid="dp-payment-method"
                className="input-base"
                value={paymentMethod}
                onChange={(e) => setPaymentMethod(e.target.value)}
              >
                <option value="especes">Espèces</option>
                <option value="wave">Wave</option>
                <option value="orange_money">Orange Money</option>
                <option value="virement">Virement</option>
                <option value="cheque">Chèque</option>
              </select>
            </div>
            <div className="text-[11px] text-[var(--text-muted)]">
              Total facture : <strong className="font-data">{(delivery.total || 0).toLocaleString("fr-FR")} F</strong>
            </div>
          </div>
        </div>
        <div className="border-t border-[var(--border-line)] p-3 flex gap-2">
          <button onClick={closeAndClear} className="btn-ghost flex-1">Annuler</button>
          <button onClick={submit} disabled={saving} className="btn-primary flex-1 flex items-center justify-center gap-1.5" data-testid="dp-confirm-delivery">
            <CheckCircle2 size={16}/>{saving ? "..." : "Valider"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ----- Cash payment modal -----
function PaymentModal({ delivery, token, onClose, onDone }) {
  const [amount, setAmount] = useState(delivery.balance);
  const [method, setMethod] = useState("especes");
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    const a = Number(amount);
    if (!a || a <= 0) return toast.error("Montant invalide");
    setSaving(true);
    try {
      await axios.post(`${API}/driver-portal/${token}/orders/${delivery.id}/payment`, {
        amount: a, method, note: "Encaissement chauffeur",
      });
      toast.success(`Encaissé : ${fmt(a)}`);
      onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[60] flex items-center justify-center p-4" onClick={onClose} data-testid="dp-payment-modal">
      <div className="bg-white w-full sm:max-w-sm shadow-2xl rounded-2xl p-5" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-3">
          <div className="font-heading font-extrabold text-lg flex items-center gap-2">
            <Banknote size={20} className="text-[var(--primary)]"/>Encaisser
          </div>
          <button onClick={onClose}><X size={20}/></button>
        </div>
        <div className="bg-[var(--bg-soft,#f7f7f7)] rounded-lg p-3 mb-3 text-sm">
          <div className="flex justify-between"><span className="text-[var(--text-muted)]">Total</span><span className="font-data">{fmt(delivery.total)}</span></div>
          <div className="flex justify-between"><span className="text-[var(--text-muted)]">Déjà payé</span><span className="font-data">{fmt(delivery.amount_paid)}</span></div>
          <div className="flex justify-between font-bold pt-1 border-t border-[var(--border-line)] mt-1"><span>Reste dû</span><span className="font-data text-[var(--alert,#c32a2a)]">{fmt(delivery.balance)}</span></div>
        </div>
        <div className="space-y-3">
          <div>
            <label className="label-mono block mb-1">Montant (F CFA)</label>
            <input type="number" className="input-base font-data" value={amount} onChange={(e) => setAmount(e.target.value)} max={delivery.balance} data-testid="dp-payment-amount" />
          </div>
          <div>
            <label className="label-mono block mb-1">Mode</label>
            <select className="input-base" value={method} onChange={(e) => setMethod(e.target.value)} data-testid="dp-payment-method">
              <option value="especes">Espèces (à la livraison)</option>
              <option value="virement">Virement bancaire</option>
              <option value="cheque">Chèque</option>
            </select>
          </div>
        </div>
        <div className="flex gap-2 mt-4">
          <button onClick={onClose} className="btn-ghost flex-1">Annuler</button>
          <button onClick={submit} disabled={saving} className="btn-primary flex-1" data-testid="dp-confirm-payment">{saving ? "..." : "Valider"}</button>
        </div>
      </div>
    </div>
  );
}

export default function DriverPortal() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [completeFor, setCompleteFor] = useState(null);
  const [payFor, setPayFor] = useState(null);

  const load = async () => {
    try {
      const { data } = await axios.get(`${API}/driver-portal/${token}/tournee`);
      setData(data);
      setError("");
    } catch (e) {
      setError(e.response?.data?.detail || "Lien invalide ou expiré");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [token]);

  // Auto-restore CompleteModal if user was uploading a photo and PWA reloaded (iOS bug)
  useEffect(() => {
    if (!data?.deliveries?.length) return;
    try {
      for (const d of data.deliveries) {
        const raw = sessionStorage.getItem(`dp-complete-${d.id}`);
        if (raw) {
          const st = JSON.parse(raw);
          if (st && (st.signature || st.photo || st.notes)) {
            setCompleteFor(d);
            break;
          }
        }
      }
    } catch {}
  }, [data]);

  const startDelivery = async (oid) => {
    try {
      await axios.post(`${API}/driver-portal/${token}/orders/${oid}/start-delivery`);
      toast.success("Tournée démarrée");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const failDelivery = async (oid) => {
    const reason = window.prompt("Motif de l'échec :");
    if (!reason) return;
    try {
      await axios.post(`${API}/driver-portal/${token}/orders/${oid}/fail-delivery`, { reason });
      toast.success("Échec enregistré");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const stats = useMemo(() => {
    if (!data) return { total: 0, done: 0, pending: 0, failed: 0 };
    return {
      total: data.deliveries.length,
      done: data.deliveries.filter((d) => d.status === "livree").length,
      pending: data.deliveries.filter((d) => d.status === "preparee" && d.delivery_status !== "echec").length,
      failed: data.deliveries.filter((d) => d.delivery_status === "echec").length,
    };
  }, [data]);

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center text-[var(--text-muted)] font-data">Chargement de la tournée…</div>;
  }
  if (error) {
    return (
      <div className="min-h-screen bg-[#FAFBFC] flex items-center justify-center p-6">
        <div className="bg-white rounded-2xl p-8 max-w-sm w-full text-center shadow-lg">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-3"><AlertCircle size={32} className="text-red-600"/></div>
          <h1 className="font-heading text-xl font-bold mb-1">Lien invalide</h1>
          <p className="text-[var(--text-muted)] text-sm">{error}</p>
          <p className="text-xs text-[var(--text-muted)] mt-3">Demandez à votre gestionnaire de vous regénérer un nouveau lien.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAFBFC]">
      {/* Header */}
      <header className="sticky top-0 z-30 bg-white border-b border-[var(--border-line)] shadow-sm">
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center gap-3">
          <VenusLogo size={40} className="rounded-xl shadow-md shrink-0" />
          <div className="min-w-0 flex-1">
            <div className="font-heading font-extrabold tracking-tight truncate">Tournée chauffeur</div>
            <div className="label-mono truncate">{data.driver.name} · {data.organization.name}</div>
          </div>
          <button onClick={load} className="btn-ghost text-xs flex items-center gap-1" data-testid="dp-refresh"><RotateCcw size={12}/>Actualiser</button>
        </div>
      </header>

      {/* Stats */}
      <div className="max-w-3xl mx-auto px-4 py-4">
        <div className="grid grid-cols-4 gap-2 sm:gap-3 mb-4">
          <div className="bg-white rounded-xl border border-[var(--border-line)] p-3 text-center">
            <div className="text-[10px] uppercase tracking-wider text-[var(--text-muted)] mb-0.5">Total</div>
            <div className="font-heading text-2xl font-extrabold">{stats.total}</div>
          </div>
          <div className="bg-white rounded-xl border border-[var(--border-line)] p-3 text-center">
            <div className="text-[10px] uppercase tracking-wider text-[var(--text-muted)] mb-0.5">À faire</div>
            <div className="font-heading text-2xl font-extrabold text-amber-600">{stats.pending}</div>
          </div>
          <div className="bg-white rounded-xl border border-[var(--border-line)] p-3 text-center">
            <div className="text-[10px] uppercase tracking-wider text-[var(--text-muted)] mb-0.5">Livrées</div>
            <div className="font-heading text-2xl font-extrabold text-[var(--primary-dark,#8E3A6D)]">{stats.done}</div>
          </div>
          <div className="bg-white rounded-xl border border-[var(--border-line)] p-3 text-center">
            <div className="text-[10px] uppercase tracking-wider text-[var(--text-muted)] mb-0.5">Échecs</div>
            <div className="font-heading text-2xl font-extrabold text-red-600">{stats.failed}</div>
          </div>
        </div>

        {/* Deliveries list */}
        {!data.deliveries.length && (
          <div className="bg-white rounded-2xl border border-[var(--border-line)] p-8 text-center text-[var(--text-muted)]">
            <Truck size={48} className="mx-auto mb-2 opacity-40"/>
            <div className="font-medium">Aucune livraison assignée</div>
            <div className="text-sm">Votre tournée du jour est vide. Revenez plus tard.</div>
          </div>
        )}

        <div className="space-y-3">
          {data.deliveries.map((d) => {
            const isDeliveredNow = d.status === "livree" || d.status === "facturee";
            const state = isDeliveredNow ? "livree" : (d.delivery_status === "echec" ? "echec" : (d.delivery_status === "en_route" ? "en_route" : "en_attente"));
            const st = STATUS[state];
            const isDone = isDeliveredNow;
            const isFailed = state === "echec";
            const isRunning = state === "en_route";
            const canPay = isDeliveredNow && d.balance > 0;
            return (
              <div key={d.id} className="bg-white rounded-2xl border border-[var(--border-line)] overflow-hidden shadow-sm" data-testid={`dp-delivery-${d.id}`}>
                <div className="p-4 space-y-3">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <div className="font-heading font-bold text-base truncate">{d.customer_name || "—"}</div>
                      <div className="label-mono mt-0.5 text-[var(--primary-dark,#8E3A6D)]">{d.order_number}</div>
                    </div>
                    <span className={`px-2.5 py-1 rounded-full text-[10px] font-semibold uppercase tracking-wider ${st.cls}`}>{st.label}</span>
                  </div>
                  {(d.customer_phone || d.customer_address) && (
                    <div className="space-y-1.5 text-sm">
                      {d.customer_phone && (
                        <a href={`tel:${d.customer_phone}`} className="flex items-center gap-2 text-[var(--primary-dark,#8E3A6D)] hover:underline">
                          <Phone size={14}/> <span className="font-data">{d.customer_phone}</span>
                        </a>
                      )}
                      {d.customer_address && (
                        <a href={`https://maps.google.com/?q=${encodeURIComponent(d.customer_address)}`} target="_blank" rel="noreferrer" className="flex items-start gap-2 text-[var(--primary-dark,#8E3A6D)] hover:underline">
                          <MapPin size={14} className="mt-0.5 shrink-0"/> <span>{d.customer_address}</span>
                        </a>
                      )}
                    </div>
                  )}
                  <div className="flex items-center justify-between text-sm pt-2 border-t border-[var(--border-line)]">
                    <div className="flex items-center gap-1 text-[var(--text-muted)]">
                      <Package size={12}/> {d.lines_count} article(s)
                    </div>
                    <div className="font-data font-bold">{fmt(d.total)}</div>
                  </div>
                  {(d.delivery_number || d.invoice_number) && (
                    <div className="flex flex-wrap items-center gap-2 text-xs pt-1">
                      {d.delivery_number && (
                        <span className="px-2 py-0.5 bg-[var(--bg-soft,#f7f7f7)] rounded font-data text-[var(--text-muted)]">{d.delivery_number}</span>
                      )}
                      {d.invoice_number && (
                        <span className="px-2 py-0.5 bg-[var(--primary-soft,#F8E8F1)] rounded font-data text-[var(--primary-dark,#8E3A6D)] font-semibold">{d.invoice_number}</span>
                      )}
                    </div>
                  )}
                  {d.payment_method && (
                    <div className="text-xs text-[var(--text-muted)] flex items-center gap-1">
                      <Clock size={11}/> Paiement souhaité : <span className="capitalize font-medium text-[var(--text-base)]">{d.payment_method}</span>
                    </div>
                  )}
                  {isFailed && d.delivery_failure_reason && (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-2 text-xs text-red-700">
                      <strong>Échec :</strong> {d.delivery_failure_reason}
                    </div>
                  )}
                  {d.balance > 0 && isDone && (
                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-2 text-xs text-amber-800">
                      Reste à encaisser : <strong className="font-data">{fmt(d.balance)}</strong>
                    </div>
                  )}
                </div>
                {/* Action footer */}
                <div className="border-t border-[var(--border-line)] bg-[var(--bg-soft,#f7f7f7)]/40 px-3 py-2.5 flex flex-wrap gap-2">
                  {!isDone && !isRunning && !isFailed && (
                    <button onClick={() => startDelivery(d.id)} className="btn-primary flex-1 flex items-center justify-center gap-1.5 text-sm" data-testid={`dp-start-${d.id}`}>
                      <PlayCircle size={14}/>Démarrer
                    </button>
                  )}
                  {isRunning && (
                    <>
                      <button onClick={() => setCompleteFor(d)} className="btn-primary flex-1 flex items-center justify-center gap-1.5 text-sm" data-testid={`dp-deliver-${d.id}`}>
                        <CheckCircle2 size={14}/>Livrée
                      </button>
                      <button onClick={() => failDelivery(d.id)} className="btn-ghost flex items-center justify-center gap-1.5 text-sm text-red-600" data-testid={`dp-fail-${d.id}`}>
                        <XCircle size={14}/>Échec
                      </button>
                    </>
                  )}
                  {canPay && (
                    <button onClick={() => setPayFor(d)} className="btn-primary flex-1 flex items-center justify-center gap-1.5 text-sm" data-testid={`dp-pay-${d.id}`}>
                      <Banknote size={14}/>Encaisser {fmt(d.balance)}
                    </button>
                  )}
                  {isDone && d.balance === 0 && (
                    <div className="flex-1 text-center text-xs text-[var(--primary-dark,#8E3A6D)] font-semibold flex items-center justify-center gap-1">
                      <CheckCircle2 size={14}/>Livraison terminée
                    </div>
                  )}
                  {isFailed && (
                    <div className="flex-1 text-center text-xs text-red-600 font-semibold">Tournée à reprendre après contact admin</div>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        <div className="mt-6 text-center text-xs text-[var(--text-muted)]">
          Lien personnel — ne partagez pas avec d'autres personnes.
        </div>
      </div>

      {completeFor && <CompleteModal delivery={completeFor} token={token} onClose={() => setCompleteFor(null)} onDone={() => { setCompleteFor(null); load(); }} />}
      {payFor && <PaymentModal delivery={payFor} token={token} onClose={() => setPayFor(null)} onDone={() => { setPayFor(null); load(); }} />}
    </div>
  );
}
